import { useInterval } from 'hooks/usePolling';
import { useStore } from 'hooks/useStore';
import { ClearTime, ClearTimePayload } from 'Interfaces/ReferenceInterfaces/ClearTimeInterface';
import { DataTableStateEvent } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';

interface FormData {
  roomTypeId: number;
  cleanTypeId: number;
  outgoingJobTypeId: number;
  incomingJobTypeId: number;
  clearTimeMinutes: number;
}

export const useClearTimeTable = (pageSize: number = 5) => {
  const { clearTimeStore, roomStore, cleanTypeStore, runTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<ClearTime | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemMenu = useRef<any>(null);
  const isClosingModalRef = useRef(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    clearTimeStore.fetchClearTimes(currentPage, pageSize);
    roomStore.fetchRooms();
    roomStore.fetchRoomTypes();
    cleanTypeStore.fetchCleanTypes();
    runTypeStore.fetchRunTypes();
  }, [currentPage, pageSize]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await clearTimeStore.pollClearTimes(currentPage, pageSize);
      await roomStore.pollRoomTypes();
    } catch (error) {
      if (environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Clear Time data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1;
      setCurrentPage(newPage);
      clearTimeStore.fetchClearTimes(newPage, 5);
    }
  };

  // Prepare room options from roomStore room types
  const roomOptions = roomStore.roomTypeOptions;

  // Prepare clean type options from cleanTypeStore
  const cleanTypeOptions = cleanTypeStore.cleanTypes.map(cleanType => ({
    label: cleanType.description || '',
    value: cleanType.id
  }));

  // Prepare outgoing job type options from runTypeStore
  const outgoingJobTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  // Prepare incoming job type options from runTypeStore
  const incomingJobTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      const updatePayload: ClearTimePayload = {
        Room_Type: formData.roomTypeId,
        CleanType: formData.cleanTypeId,
        outgoing_job_type: formData.outgoingJobTypeId,
        incoming_job_type: formData.incomingJobTypeId,
        clear_time_minutes: formData.clearTimeMinutes
      };

      const success = await clearTimeStore.updateClearTime(selectedRow.id, updatePayload);
      if (!success) {
        throw new Error('Failed to update clear time');
      }
    } else {
      const createPayload: ClearTimePayload = {
        Room_Type: formData.roomTypeId,
        CleanType: formData.cleanTypeId,
        outgoing_job_type: formData.outgoingJobTypeId,
        incoming_job_type: formData.incomingJobTypeId,
        clear_time_minutes: formData.clearTimeMinutes,
        flag: true
      };

      const success = await clearTimeStore.createClearTime(createPayload);
      if (!success) {
        throw new Error('Failed to create clear time');
      }
    }
    // Store methods already fetch updated data - no need to fetch again
    // Don't clear selectedRow here - let the modal's onHide handle cleanup
  };

  const handleDeleteRow = async () => {
    if (selectedRow) {
      await clearTimeStore.deleteClearTime(selectedRow.id);
      setSelectedRow(null);
      setShowDeleteModal(false);
      await clearTimeStore.fetchClearTimes(currentPage, pageSize);
    }
  };

  const prepareFormData = (row: ClearTime): FormData => {
    return {
      roomTypeId: row.Room_Type?.id || 0,
      cleanTypeId: row.CleanType.id || 0,
      outgoingJobTypeId: row.outgoing_job_type.id || 0,
      incomingJobTypeId: row.incoming_job_type.id || 0,
      clearTimeMinutes: row.clear_time_minutes || 0
    };
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filteredClearTimeData = clearTimeStore.clearTimes.filter(item => {
    if (!searchQuery.trim()) {
      return true;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      (item.Room_Type?.description || '').toLowerCase().includes(searchLower) ||
      item.CleanType.description.toLowerCase().includes(searchLower) ||
      item.outgoing_job_type.description.toLowerCase().includes(searchLower) ||
      item.incoming_job_type.description.toLowerCase().includes(searchLower) ||
      item.clear_time_minutes.toString().includes(searchQuery)
    );
  });

  const handleExportToExcel = () => {
    const exportData = filteredClearTimeData.map(item => ({
      'Room Type': item.Room_Type?.description || '',
      'Outgoing Job Type': item.outgoing_job_type.description,
      'Incoming Job Type': item.incoming_job_type.description,
      'Clean Type': item.CleanType.description,
      'Clear Time (mins)': item.clear_time_minutes
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clear Times');
    XLSX.writeFile(workbook, 'clear_times_export.xlsx');
  };

  // Wrapped function to safely handle modal state changes
  const handleModalClose = () => {
    isClosingModalRef.current = true;
    setShowAddModal(false);
    setSelectedRow(null);
    // Reset the closing flag after a short delay to allow for store updates
    setTimeout(() => {
      isClosingModalRef.current = false;
    }, 100);
  };

  const handleModalOpen = () => {
    // Prevent reopening if we're in the process of closing
    if (!isClosingModalRef.current) {
      setShowAddModal(true);
    }
  };

  return {
    clearTimeData: filteredClearTimeData,
    isLoading: clearTimeStore.isLoading || roomStore.roomTypesLoading,
    pagination: clearTimeStore.pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal: handleModalOpen,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    roomOptions,
    cleanTypeOptions,
    outgoingJobTypeOptions,
    incomingJobTypeOptions,
    onPageChange,
    currentPage,
    searchQuery,
    handleSearchChange,
    handleExportToExcel,
    handleModalClose
  };
};
